import { APP_PERMISSIONS, type Permission } from "@spiriidk/connect-configs";

/** Recursively collects every string leaf of a (possibly nested) permission group. */
function flattenPermissions(value: unknown): string[] {
  if (typeof value === "string") return [value];
  if (value && typeof value === "object") {
    return Object.values(value).flatMap(flattenPermissions);
  }
  return [];
}

// Every permission-group leaf in the catalog, excluding ROLES (role labels,
// not permissions — see the doc comment below on why only one role is kept).
const ALL_CATALOG_PERMISSIONS = Object.entries(APP_PERMISSIONS)
  .filter(([group]) => group !== "ROLES")
  .flatMap(([, value]) => flattenPermissions(value)) as Permission[];

/**
 * The full grant: System Admin plus every leaf permission in the
 * `APP_PERMISSIONS` catalog. This covers every page-level and fine-grained
 * permission, including the two the smoke routes gate on
 * (`permission_truckcharge_view` for connect `/bookings/chargers`,
 * `permission_locations_findorder` for the tech-ui `/order-lookup` view). Only
 * the System Admin role label is included — other role labels are not
 * permissions, and holding several simultaneously could trip role-switch logic.
 */
const FULL_PERMISSIONS: Permission[] = Array.from(
  new Set<Permission>([
    APP_PERMISSIONS.ROLES.SYSTEM_ADMIN,
    ...ALL_CATALOG_PERMISSIONS,
  ])
);

/** Company id the default (company-scoped) session carries. */
const DEFAULT_COMPANY_ID = 1;

/**
 * Auth-profile seam for the hermetic e2e suite (FRO-363, design D5 / adr/0003
 * env-gated mock auth). One seeded user, selectable per Playwright test along
 * two axes so the migrated settings views' permission/scope gates can be
 * exercised without a real Keycloak or a second seeded user:
 *
 * - `profile` selects the PERMISSION SET — the full grant, or the full grant
 *   with one settings permission removed (to drive a route/tab/card gate).
 * - `scope` selects COMPANY vs OPERATOR scope — a company-scoped session carries
 *   a permitted company id; an operator-scoped one carries none. The branding
 *   cards gate on this (logo is operator-wide; the display name is a per-company
 *   override — research/01 §1.5).
 *
 * `AUTH_PERMISSION_PROFILES` maps each profile to the permissions REMOVED from
 * the full grant, so the default profile ("full") is byte-for-byte today's
 * full-access grant and existing specs are unaffected.
 */
const AUTH_PERMISSION_PROFILES = {
  full: [],
  "no-page-settings": [APP_PERMISSIONS.PAGE.SETTINGS],
  "no-branding-tab": [APP_PERMISSIONS.SETTINGS.BRANDING],
  "no-connectlogo": [APP_PERMISSIONS.SETTINGS.BRANDING_CONNECTLOGO],
  "no-brandname": [APP_PERMISSIONS.SETTINGS.BRAND_NAME],
} satisfies Record<string, Permission[]>;

type AuthPermissionProfile = keyof typeof AUTH_PERMISSION_PROFILES;

const AUTH_SCOPES = ["company", "operator"] as const;
type AuthScope = (typeof AUTH_SCOPES)[number];

interface AuthProfileSelection {
  profile: AuthPermissionProfile;
  scope: AuthScope;
}

/**
 * The default selection: the full grant, company-scoped. `buildTestUser` of this
 * selection reproduces the pre-seam test user exactly, so groups 1–5 specs and
 * behavior are untouched by the seam.
 */
const DEFAULT_AUTH_PROFILE: AuthProfileSelection = {
  profile: "full",
  scope: "company",
};

const AUTH_PERMISSION_PROFILE_NAMES = Object.keys(
  AUTH_PERMISSION_PROFILES
) as AuthPermissionProfile[];

/**
 * Seed data for the mock-auth test user (see `./mock-auth`).
 *
 * `roles` is the flat array Spirii Keycloak stores under
 * `tokenParsed["resource-access"].techportal.roles` — it mixes role labels
 * (e.g. "System Admin") with fine-grained permission strings, and
 * `resolveAuthData` exposes the whole array as `userPermissions`.
 */
interface TestUser {
  userId: string;
  username: string;
  email: string;
  name: string;
  operatorId: number;
  companyId: number;
  // Flat list of Keycloak techportal roles/permissions (plain strings, as they
  // appear in the token); resolveAuthData exposes them as userPermissions.
  roles: Permission[];
}

/** Fields that never vary across profiles. */
const BASE_TEST_USER = {
  userId: "e2e-test-user",
  username: "e2e@spirii.com",
  email: "e2e@spirii.com",
  name: "E2E Test User",
  operatorId: 1,
} as const;

/**
 * Build the seeded test user for a profile selection. The permission set is the
 * full grant minus the profile's removed permissions; a company-scoped session
 * carries `DEFAULT_COMPANY_ID`, an operator-scoped one carries `0` (falsy — no
 * permitted company).
 */
function buildTestUser({ profile, scope }: AuthProfileSelection): TestUser {
  const removed = new Set<Permission>(AUTH_PERMISSION_PROFILES[profile]);
  return {
    ...BASE_TEST_USER,
    companyId: scope === "company" ? DEFAULT_COMPANY_ID : 0,
    roles: FULL_PERMISSIONS.filter((permission) => !removed.has(permission)),
  };
}

/** The default (full-access, company-scoped) test user. */
const TEST_USER: TestUser = buildTestUser(DEFAULT_AUTH_PROFILE);

export {
  TEST_USER,
  buildTestUser,
  DEFAULT_AUTH_PROFILE,
  AUTH_SCOPES,
  AUTH_PERMISSION_PROFILE_NAMES,
};
export type {
  TestUser,
  AuthProfileSelection,
  AuthPermissionProfile,
  AuthScope,
};
